import React, { ChangeEvent, ReactElement, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import { RootState } from '../../../redux/reducers';
import { OrderDto, OrderStatus, ReceiptType } from '../../../api/ClientApi';
import {
    clearReceipt,
    createNewEmptyDeliveryReceipt,
    createNewEmptyReceptionReceipt,
    createNewEmptyReturnReceipt,
    createNewEmptyTransferReceipt,
    importReceipt,
    saveTransferReceipt,
    saveReceptionReceipt,
    updateReceiptDescription,
    updateReceiptExecutionDate,
    updateReceptionBatchId,
} from '../../../redux/actions/receiptActions';
import { Box, Typography, DialogContent, Grid2, TextField, Tooltip } from '@mui/material';
import { CompleteOrderType, ReceiptTypeSR } from '../../../common/Types';
import { SaveEntityStatus } from '../../../redux/reducers/common';
import { getClientNameInfo, getSiteOrdersTableData } from '../OrdersHelper';
import { LocalizationProvider, DateTimePicker, renderTimeViewClock } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/sr';
import { getReceiptTypeBasedOnOrder } from './ReceiptFormHelper';
import { clearBareGoods, clearOrder, importOrder } from '../../../redux/actions/orderActions';
import OrderFormGoods from './OrderFormGoods';
import ReceptionOrderFormGoods from './ReceptionOrderFormGoods';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { FormActions, FormDialogTitle } from '../../../common/components/form/FormPrimitives';

import { ResponsiveDialog } from '../../../common/components/mobile/MobilePrimitives';

type ReceiptFormProps = {
    closeForm(): void;
    open: boolean;
    receiptId?: number;
    orderId?: number;
};

export default function ReceiptForm(props: ReceiptFormProps): ReactElement {
    const { open, closeForm, receiptId, orderId } = props;

    const order = useAppSelector((state: RootState) => state.orderForm.order);

    const receipt = useAppSelector((state: RootState) => state.receiptForm.receipt);
    const saveStatus = useAppSelector((state: RootState) => state.receiptForm.saveStatus);

    const batchId = useAppSelector((state: RootState) => state.receiptForm.receptionBatchId);

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (saveStatus === SaveEntityStatus.Saved) {
            handleClose();
        }
    }, [dispatch, saveStatus]);

    useEffect(() => {
        if (!!orderId && open) {
            dispatch(importOrder(orderId));
        }
    }, [dispatch, open, orderId]);

    useEffect(() => {
        if (open) {
            if (!receipt && !!order) {
                if (!!receiptId && receiptId > 0) {
                    dispatch(importReceipt(receiptId));
                } else {
                    const newOrder = new OrderDto(order);

                    const receiptType = getReceiptTypeBasedOnOrder(newOrder);

                    if (receiptType === ReceiptType.Delivery) {
                        dispatch(createNewEmptyDeliveryReceipt());
                    } else if (receiptType === ReceiptType.Reception) {
                        dispatch(createNewEmptyReceptionReceipt());
                    } else if (receiptType === ReceiptType.Return) {
                        dispatch(createNewEmptyReturnReceipt());
                    } else if (receiptType === ReceiptType.Transfer) {
                        dispatch(createNewEmptyTransferReceipt());
                    }
                }
            }
        }
    }, [dispatch, receiptId, order, open]);

    const sites = useAppSelector((state: RootState) => state.sites.sites);
    const clients = useAppSelector((state: RootState) => state.clients.clients);

    const [originInfo, setOriginInfo] = useState<string>('/');
    const [destinationInfo, setDestinationInfo] = useState<string>('/');

    useEffect(() => {
        if (!!order) {
            const destinationSite = order.destinationSite;
            const originSite = order.originSite;
            const supplier = order.supplier;

            const destinationData = !destinationSite
                ? '/'
                : getSiteOrdersTableData(destinationSite, sites ?? undefined, clients ?? undefined);

            const originData = !originSite
                ? !supplier
                    ? '/'
                    : (getClientNameInfo(supplier) ?? '/')
                : getSiteOrdersTableData(originSite, sites ?? undefined, clients ?? undefined);

            setOriginInfo(originData);
            setDestinationInfo(destinationData);
        }
    }, [order]);

    const handleExecutionDateChange = (date: Dayjs | null) => {
        dispatch(updateReceiptExecutionDate(date ? date.toDate() : undefined));
    };

    const handleDescriptionChange = (e: ChangeEvent<HTMLInputElement>) => {
        dispatch(updateReceiptDescription(e.target.value));
    };

    const handleReceptionBatchIdChange = (e: ChangeEvent<HTMLInputElement>) => {
        dispatch(updateReceptionBatchId(e.target.value));
    };

    const handleSave = () => {
        if (receipt?.receiptType === ReceiptType.Reception) {
            dispatch(saveReceptionReceipt(orderId ?? 0));
        } else {
            dispatch(saveTransferReceipt(orderId ?? 0));
        }
    };

    const handleClose = () => {
        dispatch(clearOrder());
        dispatch(clearBareGoods());
        dispatch(clearReceipt());
        closeForm();
    };

    const isBatchIdValid =
        receipt?.receiptType !== ReceiptType.Reception ||
        !order?.purchaseOrderId ||
        order.purchaseOrderId < 0 ||
        (!!batchId && batchId !== '');

    return (
        <ResponsiveDialog scroll="body" maxWidth="md" open={open} onClose={handleClose}>
            <FormDialogTitle>
                {receipt?.receiptType ? ReceiptTypeSR[receipt.receiptType] : '/'}{' '}
                {receipt?.receiptType === ReceiptType.Reception &&
                    !!order?.purchaseOrderId &&
                    order.purchaseOrderId > 0 && (
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            -{' '}
                            <span style={{ textDecoration: 'underline', marginLeft: '5px' }}>
                                {order.purchaseOrder?.documentCode ?? '/'}
                            </span>
                            <Tooltip title="Br. dokumenta sa narudžbenice">
                                <InfoOutlinedIcon color="primary" sx={{ ml: '10px' }} />
                            </Tooltip>
                        </Box>
                    )}
            </FormDialogTitle>
            <DialogContent dividers sx={{ px: { xs: 2, sm: 5 }, pt: { xs: 3, sm: 5 } }}>
                <Grid2 container spacing={2} style={{ maxWidth: '800px', minWidth: '600px', margin: 'auto' }}>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="sr">
                            <DateTimePicker
                                label="Datum izvršenja"
                                viewRenderers={{
                                    hours: renderTimeViewClock,
                                    minutes: renderTimeViewClock,
                                    seconds: renderTimeViewClock,
                                }}
                                value={receipt?.executionDate ? dayjs(receipt?.executionDate) : null}
                                onChange={handleExecutionDateChange}
                            />
                        </LocalizationProvider>
                    </Grid2>
                    {receipt?.receiptType === ReceiptType.Reception &&
                    !!order?.purchaseOrderId &&
                    order.purchaseOrderId > 0 ? (
                        <Grid2 size={{ xs: 12, sm: 6 }}>
                            <TextField
                                error={!!batchId && batchId !== '' ? false : true}
                                required
                                fullWidth
                                name="uniqueBatchId"
                                label="Br. fakture"
                                value={batchId ?? ''}
                                variant="outlined"
                                onChange={handleReceptionBatchIdChange}
                            />
                        </Grid2>
                    ) : (
                        <Grid2 size={{ xs: 0, sm: 6 }} />
                    )}
                    <Grid2 size={{ xs: 0, sm: 12 }} style={{ height: 15, margin: 0, padding: 0 }} />
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1" component="h6" style={{ fontSize: 18 }}>
                            Vrsta naloga:{' '}
                            <span style={{ fontWeight: 'bold' }}>
                                {order?.orderStatus === OrderStatus.Idle ||
                                order?.orderStatus === OrderStatus.Pending ||
                                order?.orderStatus === OrderStatus.Approved
                                    ? 'Nalog za otpremanje robe'
                                    : order?.orderStatus === OrderStatus.Transport ||
                                        order?.orderStatus === OrderStatus.Delivered
                                      ? 'Nalog za prijem robe'
                                      : '/'}
                            </span>
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 0, sm: 6 }} />
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1" component="h6" style={{ fontSize: 18 }}>
                            Izvor: <span style={{ fontWeight: 'bold' }}>{originInfo ?? '/'}</span>
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1" component="h6" style={{ fontSize: 18 }}>
                            Odredište: <span style={{ fontWeight: 'bold' }}>{destinationInfo ?? '/'}</span>
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 0, sm: 12 }} style={{ height: 15, margin: 0, padding: 0 }} />

                    <Grid2 size={{ xs: 12 }}>
                        <Typography
                            variant="body1"
                            component="h6"
                            style={{ fontSize: 20, borderBottom: '1px solid rgba(155,155,155,1)' }}
                        >
                            Roba
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        {receipt?.receiptType === ReceiptType.Reception ? (
                            <ReceptionOrderFormGoods />
                        ) : (
                            <OrderFormGoods
                                completeOrderType={CompleteOrderType.Transfer}
                                isOriginChosen={!!order?.originSiteId && order.originSiteId > 0}
                                isReceipt
                            />
                        )}
                    </Grid2>
                    <Grid2 size={{ xs: 0, sm: 12 }} style={{ height: 15, margin: 0, padding: 0 }} />
                    <Grid2 size={{ xs: 12 }}>
                        <TextField
                            required={false}
                            fullWidth
                            name="description"
                            label="Napomena"
                            multiline
                            rows={6}
                            value={receipt?.description ?? ''}
                            variant="outlined"
                            onChange={handleDescriptionChange}
                        />
                    </Grid2>

                    <Grid2 size={{ xs: 0, sm: 12 }} style={{ height: 15, margin: 0, padding: 0 }} />
                    <FormActions
                        onCancel={handleClose}
                        onSubmit={handleSave}
                        submitLabel="Sačuvaj"
                        disabled={saveStatus === SaveEntityStatus.Pending || !isBatchIdValid}
                        pending={saveStatus === SaveEntityStatus.Pending}
                    />
                </Grid2>
            </DialogContent>
        </ResponsiveDialog>
    );
}
